import React, { useEffect, useState } from "react";
import { Loader2, Save, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const WORK_TYPES = ["full-time", "part-time", "freelance", "gig", "internship", "contract", "remote", "hybrid", "on-site"];
const AVAIL = [
  { v: "now", l: "🟢 Available now" },
  { v: "week", l: "🟡 Available this week" },
  { v: "date", l: "🔵 Available from date" },
  { v: "none", l: "⚫ Not currently available" },
];

const Field = ({ label, children }) => (
  <div><label className="text-sm font-semibold text-navy">{label}</label><div className="mt-1">{children}</div></div>
);
const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/20";

export default function CandidateProfile() {
  const { t } = useLang();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/candidates/me").then((r) => setP(r.data)).catch(() => {}); }, []);

  const upd = (k, v) => setP({ ...p, [k]: v });
  const toggleWork = (w) => {
    const arr = p.work_types || [];
    upd("work_types", arr.includes(w) ? arr.filter((x) => x !== w) : [...arr, w]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: p.name, photo_url: p.photo_url, country: p.country, city: p.city,
        language: p.language, job_title: p.job_title, industry: p.industry,
        skills: typeof p.skills === "string" ? p.skills.split(",").map((s) => s.trim()).filter(Boolean) : p.skills,
        experience_years: Number(p.experience_years) || 0, education: p.education, bio: p.bio,
        portfolio: p.portfolio, resume_url: p.resume_url, work_types: p.work_types,
        availability: p.availability, expected_salary: p.expected_salary, public: p.public,
      };
      const { data } = await api.put("/candidates/me", payload);
      setP(data); toast.success("Profile saved");
    } catch (e) { toast.error("Could not save"); }
    finally { setBusy(false); }
  };

  if (!p) return <Layout><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div></Layout>;
  const skillsStr = Array.isArray(p.skills) ? p.skills.join(", ") : (p.skills || "");

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-extrabold text-navy">Edit Profile</h1>
          <Link to={`/u/${p.slug}`} data-testid="view-public-profile" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-navy hover:bg-brand-bluelight"><Share2 className="h-4 w-4" /> Public view</Link>
        </div>

        <div className="mt-6 gjt-card p-6 space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Full name"><input data-testid="profile-name" className={inputCls} value={p.name || ""} onChange={(e) => upd("name", e.target.value)} /></Field>
            <Field label="Job title"><input data-testid="profile-title" className={inputCls} value={p.job_title || ""} onChange={(e) => upd("job_title", e.target.value)} /></Field>
            <Field label="City"><input data-testid="profile-city" className={inputCls} value={p.city || ""} onChange={(e) => upd("city", e.target.value)} /></Field>
            <Field label="Country"><input data-testid="profile-country" className={inputCls} value={p.country || ""} onChange={(e) => upd("country", e.target.value)} /></Field>
            <Field label="Industry"><input data-testid="profile-industry" className={inputCls} value={p.industry || ""} onChange={(e) => upd("industry", e.target.value)} /></Field>
            <Field label="Experience (years)"><input data-testid="profile-exp" type="number" className={inputCls} value={p.experience_years || ""} onChange={(e) => upd("experience_years", e.target.value)} /></Field>
            <Field label="Expected salary"><input data-testid="profile-salary" className={inputCls} value={p.expected_salary || ""} onChange={(e) => upd("expected_salary", e.target.value)} /></Field>
            <Field label="Photo URL"><input data-testid="profile-photo" className={inputCls} value={p.photo_url || ""} onChange={(e) => upd("photo_url", e.target.value)} /></Field>
            <Field label="Portfolio URL"><input data-testid="profile-portfolio" className={inputCls} value={p.portfolio || ""} onChange={(e) => upd("portfolio", e.target.value)} /></Field>
            <Field label="Resume URL"><input data-testid="profile-resume" className={inputCls} value={p.resume_url || ""} onChange={(e) => upd("resume_url", e.target.value)} /></Field>
          </div>
          <Field label="Skills (comma separated)"><input data-testid="profile-skills" className={inputCls} value={skillsStr} onChange={(e) => upd("skills", e.target.value)} placeholder="Sales, Marketing, AI Tools" /></Field>
          <Field label="About you"><textarea data-testid="profile-bio" rows={3} className={inputCls} value={p.bio || ""} onChange={(e) => upd("bio", e.target.value)} /></Field>

          <Field label="Availability">
            <select data-testid="profile-availability" className={inputCls} value={p.availability || "none"} onChange={(e) => upd("availability", e.target.value)}>
              {AVAIL.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
            </select>
          </Field>

          <div>
            <label className="text-sm font-semibold text-navy">Work preferences</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {WORK_TYPES.map((w) => (
                <button key={w} type="button" data-testid={`worktype-${w}`} onClick={() => toggleWork(w)}
                  className={`rounded-full px-3 py-1.5 text-sm font-medium capitalize transition-colors ${(p.work_types || []).includes(w) ? "bg-brand-blue text-white" : "bg-slate-100 text-slate-600"}`}>{w}</button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm font-medium text-navy">
            <input data-testid="profile-public" type="checkbox" checked={p.public !== false} onChange={(e) => upd("public", e.target.checked)} className="h-4 w-4" />
            Make my profile publicly discoverable (SEO)
          </label>

          <button onClick={save} disabled={busy} data-testid="profile-save" className="gjt-btn-orange w-full rounded-xl py-3 text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile
          </button>
        </div>
      </div>
    </Layout>
  );
}
